import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { CloseIcon, JsonDialog, PublishDialog, TextDialog } from '../components/prompts/PromptDialogs';
import {
  FUNCTION_TEMPLATE,
  MESSAGE_ROLES,
  REASONING_EFFORTS,
  SCHEMA_TEMPLATE,
  SERVICE_TIERS,
  SUMMARIES,
  TEXT_FORMATS,
  VERBOSITIES,
  draftFromVersion,
  draftToBody,
  emptyDraft,
  formatJson,
  isSameDraft,
  isVariableName,
  parseFunction,
  parseObject,
  schemaOf,
  setSetting,
  textFormatOf,
  usesReasoning,
  variablesIn,
  withTextFormat,
} from '../components/prompts/promptModel';
import './PromptVersions.css';

const NEW_PROMPT = '__new_prompt__';
const OTHER_MODEL = '__other_model__';

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '—');

const ExpandIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M9.5 2.5h4v4M6.5 13.5h-4v-4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <path d="M7 1.5v11M1.5 7h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const Section = ({ title, children }) => (
  <details className="pe-section" open>
    <summary>{title}</summary>
    <div className="pe-section-body">{children}</div>
  </details>
);

/** A label on the left and a dropdown on the right. A saved value missing from the list still shows. */
const SelectRow = ({ label, value, options, onChange }) => {
  const known = options.some((option) => option.value === value);
  return (
    <label className="pe-row">
      <span>{label}</span>
      <select className="pe-select" value={value} onChange={(event) => onChange(event.target.value)}>
        {!known && <option value={value}>{value}</option>}
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
};

const optionsFrom = (values, emptyLabel) => [
  ...(emptyLabel ? [{ value: '', label: emptyLabel }] : []),
  ...values.map((value) => ({ value, label: value })),
];

const NumberRow = ({ label, value, step, onChange }) => {
  const [text, setText] = useState(value === undefined ? '' : String(value));

  // Follows a value changed from outside, without disturbing what is being typed.
  useEffect(() => {
    setText((current) => (Number(current) === value ? current : (value === undefined ? '' : String(value))));
  }, [value]);

  return (
    <label className="pe-row">
      <span>{label}</span>
      <input
        className="pe-input is-number"
        type="number"
        step={step}
        value={text}
        placeholder="Default"
        onChange={(event) => {
          setText(event.target.value);
          onChange(event.target.value === '' ? undefined : Number(event.target.value));
        }}
      />
    </label>
  );
};

const SwitchRow = ({ label, checked, onChange }) => (
  <div className="pe-row">
    <span id={`pe-switch-${label}`}>{label}</span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={`pe-switch-${label}`}
      className={checked ? 'pe-switch is-on' : 'pe-switch'}
      onClick={() => onChange(!checked)}
    >
      <span />
    </button>
  </div>
);

const ModelRow = ({ value, models, onChange }) => {
  const [typing, setTyping] = useState(false);
  const known = models.includes(value);

  if (typing || !known) {
    return (
      <label className="pe-row">
        <span>Model</span>
        <input
          className="pe-input is-model"
          value={value}
          placeholder="Model name"
          spellCheck={false}
          onChange={(event) => onChange(event.target.value)}
          onBlur={() => setTyping(false)}
        />
      </label>
    );
  }
  return (
    <SelectRow
      label="Model"
      value={value}
      options={[...optionsFrom(models), { value: OTHER_MODEL, label: 'Other…' }]}
      onChange={(next) => {
        if (next === OTHER_MODEL) {
          setTyping(true);
        } else {
          onChange(next);
        }
      }}
    />
  );
};

const ModelSection = ({ draft, models, updateSettings, setModel, onEditSchema, onEditSettings }) => {
  const { model, settings } = draft;
  const format = textFormatOf(settings);
  const reasoning = usesReasoning(model);
  const set = (path) => (value) => updateSettings((current) => setSetting(current, path, value === '' ? undefined : value));

  return (
    <Section title="Model">
      <ModelRow value={model} models={models} onChange={setModel} />
      <SelectRow
        label="Service tier"
        value={settings.service_tier || ''}
        options={optionsFrom(SERVICE_TIERS.filter((tier) => tier !== 'default'), 'Standard')}
        onChange={set(['service_tier'])}
      />
      <SelectRow
        label="Text format"
        value={format}
        options={optionsFrom(TEXT_FORMATS)}
        onChange={(next) => updateSettings((current) => withTextFormat(current, next))}
      />
      {format === 'json_schema' && (
        <div className="pe-row is-end">
          <button type="button" className="pe-chip is-button" onClick={onEditSchema}>
            <span className="pe-chip-mark">{'{ }'}</span>{schemaOf(settings).name || 'Response schema'}
          </button>
        </div>
      )}
      {(reasoning || settings.reasoning?.effort) && (
        <SelectRow
          label="Reasoning effort"
          value={settings.reasoning?.effort || ''}
          options={optionsFrom(REASONING_EFFORTS, 'Default')}
          onChange={set(['reasoning', 'effort'])}
        />
      )}
      {(reasoning || settings.text?.verbosity) && (
        <SelectRow
          label="Verbosity"
          value={settings.text?.verbosity || ''}
          options={optionsFrom(VERBOSITIES, 'Default')}
          onChange={set(['text', 'verbosity'])}
        />
      )}
      {(reasoning || settings.reasoning?.summary) && (
        <SelectRow
          label="Summary"
          value={settings.reasoning?.summary || ''}
          options={optionsFrom(SUMMARIES, 'Off')}
          onChange={set(['reasoning', 'summary'])}
        />
      )}
      {(!reasoning || settings.temperature !== undefined) && (
        <NumberRow label="Temperature" step="0.1" value={settings.temperature} onChange={set(['temperature'])} />
      )}
      {(!reasoning || settings.top_p !== undefined) && (
        <NumberRow label="Top P" step="0.1" value={settings.top_p} onChange={set(['top_p'])} />
      )}
      {(!reasoning || settings.max_output_tokens !== undefined) && (
        <NumberRow label="Max tokens" step="1" value={settings.max_output_tokens} onChange={set(['max_output_tokens'])} />
      )}
      <SwitchRow label="Store logs" checked={settings.store === true} onChange={(next) => updateSettings((current) => setSetting(current, ['store'], next))} />
      <button type="button" className="pe-link" onClick={onEditSettings}>Edit settings as JSON</button>
    </Section>
  );
};

const VariablesSection = ({ messages, onAdd }) => {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const variables = variablesIn(messages);
  const valid = isVariableName(name) && !variables.includes(name);

  const add = () => {
    if (valid) {
      onAdd(name);
      setName('');
      setAdding(false);
    }
  };

  return (
    <Section title="Variables">
      <div className="pe-chips">
        {variables.map((variable) => <span key={variable} className="pe-chip">{variable}</span>)}
        {adding ? (
          <input
            className="pe-input is-chip"
            aria-label="Variable name"
            placeholder="name"
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                add();
              }
              if (event.key === 'Escape') {
                setAdding(false);
              }
            }}
            onBlur={() => (name ? add() : setAdding(false))}
          />
        ) : (
          <button type="button" className="pe-chip is-add" onClick={() => setAdding(true)}><PlusIcon /> Add</button>
        )}
      </div>
      <p className="pe-muted">Written in the text as <code>{'{{name}}'}</code>, and filled from what the app sends. Add puts it at the end of the prompt.</p>
    </Section>
  );
};

const FunctionsSection = ({ tools, onOpen, onRemove, onAdd }) => (
  <Section title="Functions">
    <div className="pe-chips">
      {tools.map((tool, index) => (
        <span key={tool.name} className="pe-chip is-function">
          <button type="button" className="pe-chip-open" title={tool.name} onClick={() => onOpen(index)}>
            <span className="pe-chip-mark">{'{ƒ}'}</span>{tool.name}
          </button>
          <button type="button" className="pe-chip-remove" aria-label={`Remove ${tool.name}`} onClick={() => onRemove(index)}><CloseIcon /></button>
        </span>
      ))}
      <button type="button" className="pe-chip is-add" onClick={onAdd}><PlusIcon /> Add</button>
    </div>
  </Section>
);

const MessageEditor = ({ message, title, canEditRole, onChange, onRemove, onExpand }) => (
  <div className="pe-message">
    {canEditRole && (
      <div className="pe-message-head">
        <span className="pe-field-label">{title}</span>
        <select className="pe-select is-small" aria-label={`Role of ${title}`} value={message.role} onChange={(event) => onChange({ role: event.target.value })}>
          {MESSAGE_ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
        </select>
        <label className="pe-check is-small">
          <input type="checkbox" checked={message.isParts} onChange={(event) => onChange({ isParts: event.target.checked })} />
          <span>JSON</span>
        </label>
        <div className="pe-spacer" />
        <button type="button" className="pe-icon-button" aria-label={`Remove ${title}`} onClick={onRemove}><CloseIcon /></button>
      </div>
    )}
    <div className="pe-textarea-wrap">
      <textarea
        className={`pe-textarea${canEditRole ? ' is-short' : ''}${message.isParts ? ' is-code' : ''}`}
        aria-label={title}
        placeholder={canEditRole ? 'Message' : 'Describe desired model behavior (tone, tool usage, response style)'}
        value={message.text}
        onChange={(event) => onChange({ text: event.target.value })}
      />
      <button type="button" className="pe-icon-button pe-expand" aria-label={`Expand ${title}`} onClick={onExpand}><ExpandIcon /></button>
    </div>
  </div>
);

const VersionsPanel = ({ versions, selected, busy, onOpen, onMakeDefault, onReplaceModel }) => {
  const [replacing, setReplacing] = useState(false);
  const [model, setModel] = useState('');

  useEffect(() => {
    setReplacing(false);
    setModel(selected?.model || '');
  }, [selected]);

  return (
    <aside className="pe-panel">
      <h2>Versions</h2>
      <p className="pe-muted">Each build pins the version number it was tested with, so older versions keep working.</p>
      <div className="pe-version-list">
        {versions.map((version) => {
          const isOpen = selected?.version === version.version;
          return (
            <div key={version.version} className={isOpen ? 'pe-version is-open' : 'pe-version'}>
              <button type="button" className="pe-version-head" aria-current={isOpen} onClick={() => onOpen(version.version)}>
                <strong>v{version.version}</strong>
                {version.is_default && <span className="pe-badge">default</span>}
                <span className="pe-version-model">{version.model}</span>
              </button>
              <p className="pe-muted">
                {formatDate(version.created_at)}{version.created_by ? ` · ${version.created_by}` : ''}
              </p>
              {version.note && <p className="pe-muted">{version.note}</p>}
              {isOpen && (
                <div className="pe-version-actions">
                  {!version.is_default && (
                    <button type="button" className="pe-button" disabled={busy} onClick={onMakeDefault}>Make v{version.version} the default</button>
                  )}
                  {replacing ? (
                    <div className="pe-replace">
                      <p className="pe-warning">
                        This changes the model for every build pinned to v{version.version}, within a minute and with no release.
                        Use it only when OpenAI retires a model. For anything else, publish a new version.
                      </p>
                      <input className="pe-input" aria-label="New model" spellCheck={false} value={model} onChange={(event) => setModel(event.target.value)} />
                      <div className="pe-replace-buttons">
                        <button type="button" className="pe-button" onClick={() => setReplacing(false)}>Cancel</button>
                        <button
                          type="button"
                          className="pe-button is-primary"
                          disabled={busy || !model.trim() || model.trim() === version.model}
                          onClick={() => onReplaceModel(model.trim())}
                        >
                          Replace model
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="pe-link" onClick={() => setReplacing(true)}>Replace the model of this version</button>
                  )}
                  {selected.model_updated_at && <p className="pe-muted">Model replaced {formatDate(selected.model_updated_at)}</p>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
};

const PromptVersions = () => {
  const { fetchWithAuth } = useAuth();
  const [prompts, setPrompts] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [newName, setNewName] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [publishError, setPublishError] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const isNewPrompt = newName !== null;
  const base = useMemo(() => {
    if (isNewPrompt) {
      return emptyDraft();
    }
    return selected ? draftFromVersion(selected) : null;
  }, [isNewPrompt, selected]);
  const isDirty = Boolean(draft && base && !isSameDraft(draft, base));

  const request = useCallback(async (path, options) => {
    const response = await fetchWithAuth(path, options);
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(data?.error || `Request failed (${response.status})`);
    }
    return data;
    // fetchWithAuth is recreated on every AuthContext render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPrompt = useCallback(async (name, version) => {
    const list = await request(`/api/admin/prompts/${name}/versions`);
    const target = version ?? list.find((item) => item.is_default)?.version ?? list[0]?.version;
    const opened = await request(`/api/admin/prompts/${name}/versions/${target}`);
    setVersions(list);
    setSelected(opened);
    setDraft(draftFromVersion(opened));
    setNewName(null);
  }, [request]);

  const reloadPrompts = useCallback(async () => {
    const list = await request('/api/admin/prompts');
    setPrompts(list);
    return list;
  }, [request]);

  const startNewPrompt = () => {
    setSelected(null);
    setVersions([]);
    setDraft(emptyDraft());
    setNewName('');
  };

  useEffect(() => {
    reloadPrompts()
      .then((list) => {
        if (list.length > 0) {
          return openPrompt(list[0].name);
        }
        startNewPrompt();
        return null;
      })
      .catch((loadError) => setError(loadError.message));
  }, [reloadPrompts, openPrompt]);

  const run = async (action) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (actionError) {
      setError(actionError.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmDiscard = () => !isDirty || window.confirm('Discard your unpublished changes?');

  const selectPrompt = (name) => {
    if (!confirmDiscard()) {
      return;
    }
    if (name === NEW_PROMPT) {
      startNewPrompt();
    } else {
      run(() => openPrompt(name));
    }
  };

  const selectVersion = (version) => {
    if (version !== selected?.version && confirmDiscard()) {
      run(() => openPrompt(selected.name, version));
    }
  };

  const discard = () => {
    if (confirmDiscard()) {
      setDraft(base);
      if (isNewPrompt) {
        setNewName('');
      }
    }
  };

  const publish = async (note, setDefault) => {
    const name = isNewPrompt ? newName.trim() : selected.name;
    setBusy(true);
    setPublishError(null);
    try {
      const published = await request(`/api/admin/prompts/${encodeURIComponent(name)}/versions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...draftToBody(draft), note: note || null, set_default: setDefault }),
      });
      await reloadPrompts();
      await openPrompt(name, published.version);
      setDialog(null);
    } catch (publishFailure) {
      setPublishError(publishFailure.message);
    } finally {
      setBusy(false);
    }
  };

  const changeVersion = (path, body) => {
    if (confirmDiscard()) {
      run(async () => {
        await request(path, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        await reloadPrompts();
        await openPrompt(selected.name, selected.version);
      });
    }
  };

  const updateMessage = (index, patch) => setDraft((current) => ({
    ...current,
    messages: current.messages.map((message, position) => (position === index ? { ...message, ...patch } : message)),
  }));

  const updateSettings = (change) => setDraft((current) => ({ ...current, settings: change(current.settings) }));

  const addVariable = (name) => setDraft((current) => ({
    ...current,
    messages: current.messages.map((message, position) => {
      if (position !== 0) {
        return message;
      }
      const separator = message.text && !message.text.endsWith('\n') ? '\n' : '';
      return { ...message, text: `${message.text}${separator}{{${name}}}` };
    }),
  }));

  const models = useMemo(() => {
    const names = new Set((prompts || []).map((prompt) => prompt.model));
    if (selected?.model) {
      names.add(selected.model);
    }
    return [...names].sort();
  }, [prompts, selected]);

  const nextVersion = versions.length > 0 ? versions[0].version + 1 : 1;
  const canPublish = isDirty && !busy && (!isNewPrompt || newName.trim().length > 0);

  const renderDialog = () => {
    if (!dialog || !draft) {
      return null;
    }
    const close = () => setDialog(null);

    if (dialog.type === 'text') {
      const message = draft.messages[dialog.index];
      return (
        <TextDialog
          title={dialog.index === 0 ? 'Prompt' : `Message ${dialog.index + 1}`}
          value={message.text}
          onChange={(text) => updateMessage(dialog.index, { text })}
          onClose={close}
        />
      );
    }

    if (dialog.type === 'function') {
      const isNew = dialog.index === null;
      const others = draft.tools.filter((_, position) => position !== dialog.index).map((tool) => tool.name);
      return (
        <JsonDialog
          title={isNew ? 'Add function' : 'Edit function'}
          description="The model decides when to call a function from the user's message. The app has to handle every function it can receive."
          label="Definition"
          initialText={formatJson(isNew ? FUNCTION_TEMPLATE : draft.tools[dialog.index])}
          parse={(text) => {
            const result = parseFunction(text, others);
            return result.error ? result : { value: result.tool };
          }}
          onSave={(tool) => {
            setDraft((current) => ({
              ...current,
              tools: isNew ? [...current.tools, tool] : current.tools.map((item, position) => (position === dialog.index ? tool : item)),
            }));
            close();
          }}
          onDelete={isNew ? null : () => {
            setDraft((current) => ({ ...current, tools: current.tools.filter((_, position) => position !== dialog.index) }));
            close();
          }}
          onClose={close}
        />
      );
    }

    if (dialog.type === 'schema') {
      const schema = schemaOf(draft.settings);
      return (
        <JsonDialog
          title="Response schema"
          description="The model answers in this JSON shape. The order of the properties is the order of the fields in the answer."
          label="Definition"
          initialText={formatJson(schema.schema ? schema : SCHEMA_TEMPLATE)}
          parse={parseObject}
          onSave={(value) => {
            updateSettings((current) => withTextFormat(current, 'json_schema', value));
            close();
          }}
          onClose={close}
        />
      );
    }

    if (dialog.type === 'settings') {
      return (
        <JsonDialog
          title="Settings"
          description="Sent to OpenAI with the request, as they are. Do not set model, input, tools, stream or instructions here."
          label="Settings"
          initialText={formatJson(draft.settings)}
          parse={parseObject}
          onSave={(value) => {
            updateSettings(() => value);
            close();
          }}
          onClose={close}
        />
      );
    }

    return (
      <PublishDialog
        fromLabel={isNewPrompt ? 'New prompt' : `Version ${selected.version}`}
        toLabel={`Version ${nextVersion}`}
        isFirstVersion={isNewPrompt}
        busy={busy}
        error={publishError}
        onPublish={publish}
        onClose={close}
      />
    );
  };

  return (
    <div className="prompt-page">
      <header className="pe-header">
        <div className="pe-title">
          {isNewPrompt ? (
            <input
              className="pe-input is-name"
              aria-label="Prompt name"
              placeholder="prompt_name"
              spellCheck={false}
              autoFocus
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
            />
          ) : (
            <select className="pe-title-select" aria-label="Prompt" value={selected?.name || ''} onChange={(event) => selectPrompt(event.target.value)}>
              {(prompts || []).map((prompt) => <option key={prompt.name} value={prompt.name}>{prompt.name}</option>)}
              <option value={NEW_PROMPT}>+ New prompt</option>
            </select>
          )}
          {!isNewPrompt && selected && (
            <select className="pe-version-select" aria-label="Version" value={selected.version} onChange={(event) => selectVersion(Number(event.target.value))}>
              {versions.map((version) => (
                <option key={version.version} value={version.version}>
                  v{version.version}{version.is_default ? ' · default' : ''}
                </option>
              ))}
            </select>
          )}
          {isNewPrompt && (prompts || []).length > 0 && (
            <button type="button" className="pe-link" onClick={() => selectPrompt(prompts[0].name)}>Cancel</button>
          )}
          {isDirty && <span className="pe-dirty">Unpublished changes</span>}
        </div>
        <div className="pe-actions">
          {isDirty && <button type="button" className="pe-button" onClick={discard}>Discard</button>}
          <button
            type="button"
            className="pe-button is-primary"
            disabled={!canPublish}
            onClick={() => {
              setPublishError(null);
              setDialog({ type: 'publish' });
            }}
          >
            Publish
          </button>
        </div>
      </header>

      {error && <div className="pe-alert" role="alert">{error}</div>}
      {prompts === null && !error && <p className="pe-muted pe-loading">Loading prompts…</p>}

      {draft && (
        <div className="pe-layout">
          <div className="pe-editor">
            <section className="pe-section-plain">
              <h2 className="pe-field-label">Prompt</h2>
              <MessageEditor
                message={draft.messages[0]}
                title="Prompt"
                canEditRole={false}
                onChange={(patch) => updateMessage(0, patch)}
                onExpand={() => setDialog({ type: 'text', index: 0 })}
              />
              <p className="pe-muted">{draft.messages[0].text.length.toLocaleString()} characters</p>

              {draft.messages.slice(1).map((message, offset) => {
                const index = offset + 1;
                return (
                  <MessageEditor
                    // Messages have no ids, and their position is what is sent to OpenAI.
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    message={message}
                    title={`Message ${index + 1}`}
                    canEditRole
                    onChange={(patch) => updateMessage(index, patch)}
                    onRemove={() => setDraft((current) => ({ ...current, messages: current.messages.filter((_, position) => position !== index) }))}
                    onExpand={() => setDialog({ type: 'text', index })}
                  />
                );
              })}

              <button
                type="button"
                className="pe-add-row"
                onClick={() => setDraft((current) => ({ ...current, messages: [...current.messages, { role: 'user', text: '', isParts: false }] }))}
              >
                <span>Add messages to prompt</span><PlusIcon />
              </button>
            </section>

            <ModelSection
              draft={draft}
              models={models}
              updateSettings={updateSettings}
              setModel={(model) => setDraft((current) => ({ ...current, model }))}
              onEditSchema={() => setDialog({ type: 'schema' })}
              onEditSettings={() => setDialog({ type: 'settings' })}
            />
            <VariablesSection messages={draft.messages} onAdd={addVariable} />
            <FunctionsSection
              tools={draft.tools}
              onOpen={(index) => setDialog({ type: 'function', index })}
              onAdd={() => setDialog({ type: 'function', index: null })}
              onRemove={(index) => setDraft((current) => ({ ...current, tools: current.tools.filter((_, position) => position !== index) }))}
            />
          </div>

          {!isNewPrompt && selected && (
            <VersionsPanel
              versions={versions}
              selected={selected}
              busy={busy}
              onOpen={selectVersion}
              onMakeDefault={() => changeVersion(`/api/admin/prompts/${selected.name}/default`, { version: selected.version })}
              onReplaceModel={(model) => changeVersion(`/api/admin/prompts/${selected.name}/versions/${selected.version}/model`, { model })}
            />
          )}
        </div>
      )}

      {renderDialog()}
    </div>
  );
};

export default PromptVersions;
